import { FilterIcon, PlusIcon } from '@heroicons/react/solid';
import cloneDeep from 'lodash.clonedeep';
import React, { useState } from 'react';
import { Menu, Mode } from '../Menu';
import { Pill, PillVariant } from '../Pill';
import { Select } from '../Select';
import { HeroIcons } from '../style/heroicons';
import { Text } from '../Text';
import { appendClassProps } from '../util';
import { useMobile, useMounted } from '../util/hooks';
/**
 * Filter is a controlled component that translates a [json-api](https://github.com/Qnergy/JsonApi)-compatible json object to a filter interface, and allows the user to
 * change the json object via the filter interface.
 * @param props
 * @returns
 */
const FilterRaw = ({ countFound = 0, hidePills = false, openMenusOnMount = false, className, actionComponents, dark, filter, autocompleteOptions, onSearch, onChangeFilter, onOpenFilterItem }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const filterMounted = useMounted();
    const isMobile = useMobile();
    const handleClickResetAll = () => {
        const newFilter = cloneDeep(filter);
        for (let i = 0; i < newFilter.length; i++) {
            newFilter[i].selected = false;
            for (let j = 0; j < (newFilter[i].values?.length || 0); j++) {
                if (newFilter[i].values?.[j]) {
                    newFilter[i].values[j].selected = false;
                }
                else if (newFilter[i].range) {
                    newFilter[i].range = {};
                }
            }
        }
        onChangeFilter(newFilter);
        setSearchTerm('');
    };
    const handleChangeFilter = (categoryId, value) => {
        const newFilter = cloneDeep(filter);
        for (let i = 0; i < newFilter.length; i++) {
            if (newFilter[i].id === categoryId) {
                if (Array.isArray(value)) {
                    newFilter[i].values = value;
                }
                else if (newFilter[i].range) {
                    newFilter[i].range = value ? value : {};
                }
            }
        }
        onChangeFilter(newFilter);
    };
    const handleChangeFilterCategories = (menuItems) => {
        const newFilter = cloneDeep(filter);
        for (let i = 0; i < newFilter.length; i++) {
            newFilter[i] = {
                ...newFilter[i],
                ...menuItems?.find((item) => item.id === newFilter[i].id)
            };
        }
        onChangeFilter(newFilter);
    };
    const removeFilterCategory = (id) => {
        const newFilter = cloneDeep(filter);
        for (let i = 0; i < newFilter.length; i++) {
            if (newFilter[i].id === id) {
                newFilter[i].selected = false;
                if (newFilter[i].range)
                    newFilter[i].range = {};
                if (newFilter[i].values) {
                    newFilter[i].values = newFilter[i].values?.map((val) => {
                        val.selected = false;
                        return val;
                    });
                }
            }
        }
        onChangeFilter(newFilter);
    };
    return (React.createElement("div", { className: `flex flex-row justify-between w-full flex-wrap gap-2 md:flex-nowrap md:gap-0${appendClassProps(className)}`, "data-testid": "filter" },
        React.createElement("div", { className: "flex flex-row gap-2 items-center flex-wrap" },
            !hidePills &&
                filter
                    .filter((item) => item.selected)
                    ?.map((item) => {
                    const selectedValues = item?.values?.filter((val) => val.selected);
                    let label = item.label.toString();
                    const numberSelectedValues = selectedValues?.length || 0;
                    if (item.mode === Mode.range) {
                        const min = selectedValues?.[0]?.id;
                        const max = selectedValues?.[numberSelectedValues - 1]?.id;
                        if (min !== undefined && max !== undefined) {
                            label = `${min}-${max}`;
                        }
                        else {
                            label = item.label.toString();
                        }
                    }
                    else if (numberSelectedValues > 0) {
                        label = `${selectedValues?.[0].label} ${numberSelectedValues > 1
                            ? ` and ${numberSelectedValues - 1} more...`
                            : ''}`;
                    }
                    else if (item?.range?.from || item?.range?.to) {
                        label = `${item?.range?.from instanceof Date
                            ? item?.range?.from?.toLocaleDateString()
                            : item?.range?.from?.toLocaleString()} to ${item?.range?.to instanceof Date
                            ? item?.range?.to?.toLocaleDateString()
                            : item?.range?.to?.toLocaleString()}`;
                    }
                    return (React.createElement(Menu, { key: item.id, menuItems: item.values, dateRange: item.range, mode: item.mode
                            ? item.mode
                            : item.range
                                ? Mode.dateRange
                                : Mode.multi, onChange: (newItems) => handleChangeFilter(item.id, newItems), 
                        // prevent menus from opening when the filter first renders
                        openOnMount: filterMounted && openMenusOnMount, onOpen: () => {
                            if (onOpenFilterItem)
                                onOpenFilterItem(item);
                        }, loading: item.loading },
                        React.createElement(Pill, { variant: PillVariant.secondary, leftIcon: HeroIcons.ChevronDownIcon, rightIcon: HeroIcons.XCircleIcon, propagateLeftIconClickEvent: true, onClickRightIcon: () => removeFilterCategory(item.id) }, label)));
                }),
            !hidePills && !filter.every((item) => item.selected) && (React.createElement(Menu, { menuItems: filter, onChange: handleChangeFilterCategories, closeOnSelect: true, selectAll: false }, isMobile && !filter.some((item) => item.selected) ? (React.createElement("div", { className: "bg-blue-800 cursor-pointer p-2 rounded-sm" },
                React.createElement(FilterIcon, { className: "text-white w-5 h-5" }))) : (React.createElement("div", { className: "flex flex-row items-center text-blue-800 hover:text-blue-400" },
                React.createElement(PlusIcon, { className: `h-4 w-4 ${dark ? 'text-white text-shadow' : 'text-blue-800'}` }),
                React.createElement(Text, { className: `cursor-pointer ${dark ? 'text-white text-shadow' : 'text-blue-800'}`, overrideColor: true }, "Filter"))))),
            !hidePills && filter.some((item) => item.selected) && (React.createElement(Pill, { onClick: handleClickResetAll }, "Reset All")),
            countFound ? React.createElement(Text, null, `${countFound} found`) : null,
            autocompleteOptions && (React.createElement(Select, { value: searchTerm, options: autocompleteOptions, onChangeValue: (term) => {
                    if (onSearch)
                        onSearch(term?.toString() ?? '');
                }, hideErrorSection: true, searchable: true, leftIcon: HeroIcons.SearchIcon, autoFocus: true }))),
        React.createElement("div", { className: "flex flex-row gap-2 md:justify-end items-center flex-wrap" }, actionComponents)));
};
export const Filter = React.memo(FilterRaw);
export * from './index.types';
export * from './util';
//# sourceMappingURL=index.js.map